import { DEFAULT_STATUS_COLOR, MARKER_RADIUS, STATUS_COLOR_MAP } from 'const/floorPlan';
import { Size, ViewState } from 'types/issue';

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Marker colour by status, via the shared map (see const/floorPlan.ts). */
export const colorForStatus = (status?: string | null) =>
  STATUS_COLOR_MAP[(status ?? '').toUpperCase()] ?? DEFAULT_STATUS_COLOR;

/** The screen-space box of the displayed image, given the current pan/zoom. */
export const imageBox = ({ pan, scale, base, container }: ViewState) => {
  const w = base.w * scale;
  const h = base.h * scale;
  return { left: container.w / 2 + pan.x - w / 2, top: container.h / 2 + pan.y - h / 2, w, h };
};

/** Screen point -> percentage of the image, inset so a circle stays fully inside. */
export const pointToPct = (x: number, y: number, view: ViewState) => {
  const box = imageBox(view);
  if (!box.w || !box.h) return null;
  const insetX = (MARKER_RADIUS / box.w) * 100;
  const insetY = (MARKER_RADIUS / box.h) * 100;
  return {
    xPct: clamp(((x - box.left) / box.w) * 100, insetX, 100 - insetX),
    yPct: clamp(((y - box.top) / box.h) * 100, insetY, 100 - insetY)
  };
};

/** How far the (scaled) stage can be dragged before its edge crosses the canvas edge. */
export const clampPan = (x: number, y: number, view: ViewState) => {
  const maxX = Math.max(0, (view.base.w * view.scale - view.container.w) / 2);
  const maxY = Math.max(0, (view.base.h * view.scale - view.container.h) / 2);
  return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
};

/** The image size that fits the container, at zoom 100%. */
export const fitBaseSize = (container: Size, natural: Size) => {
  if (!container.w || !container.h || !natural.w || !natural.h) return { w: 0, h: 0 };
  const fit = Math.min(container.w / natural.w, container.h / natural.h);
  return { w: natural.w * fit, h: natural.h * fit };
};
